import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { navigatorAPI } from "@/services/api";
import {
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";
import { Compass, MessageSquare, Sparkles, Target } from "lucide-react";

const NavigatorPanel = () => {
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [coaching, setCoaching] = useState(null);
  const [coachingLoading, setCoachingLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [briefRes, recRes] = await Promise.all([
        navigatorAPI.getBriefing(),
        navigatorAPI.getRecommendedCampaigns(5),
      ]);
      setBriefing(briefRes.data);
      setRecommendations(recRes.data?.recommendations || []);
    } catch {
      toast.error("Navigator unavailable");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCoach = async () => {
    if (!draft.trim()) return;
    setCoachingLoading(true);
    try {
      const res = await navigatorAPI.coachContent(channel, draft);
      setCoaching(res.data);
    } catch {
      toast.error("Content coaching failed");
    } finally {
      setCoachingLoading(false);
    }
  };

  if (loading) {
    return (
      <TailAdminPanel title="Proovam Navigator" className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-16 w-full" />
      </TailAdminPanel>
    );
  }

  return (
    <div className="space-y-4">
      <TailAdminPanel
        title="Proovam Navigator"
        description="Your copilot — you stay in control of every relationship."
        action={
          <span className="inline-flex items-center gap-1 text-xs text-brand-600">
            <Compass className="h-3.5 w-3.5" />
            BRM copilot
          </span>
        }
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Trust score</p>
            <p className="text-xl font-semibold text-gray-900">{briefing?.trust_score ?? "—"}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Pending enrollments</p>
            <p className="text-xl font-semibold text-gray-900">{briefing?.pending_enrollments ?? 0}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs text-gray-500">Month earnings</p>
            <p className="text-xl font-semibold text-gray-900">
              ₦{(briefing?.month_earnings || 0).toLocaleString()}
            </p>
          </div>
        </div>
        {briefing?.coaching_tip ? (
          <p className="mt-3 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-800">
            {briefing.coaching_tip}
          </p>
        ) : null}
      </TailAdminPanel>

      {recommendations.length > 0 ? (
        <TailAdminPanel title="Best Prove-Requests for you" description="Campaign-fit scoring">
          <div className="space-y-2">
            {recommendations.map((rec) => (
              <div
                key={rec.campaign_id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/brm/campaigns/${rec.campaign_id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") navigate(`/brm/campaigns/${rec.campaign_id}`);
                }}
                className="flex cursor-pointer items-center justify-between rounded-xl border border-gray-200 px-3 py-2 transition hover:border-brand-200 hover:bg-brand-50/40"
              >
                <div>
                  <p className="font-medium text-gray-900">{rec.title}</p>
                  <p className="text-xs text-gray-500">
                    ₦{rec.commission_per_goal?.toLocaleString()} · fit {rec.fit_score}%
                  </p>
                </div>
                <TailAdminStatusBadge tone={rec.has_joined ? "success" : "primary"}>
                  {rec.has_joined ? "Joined" : "New"}
                </TailAdminStatusBadge>
              </div>
            ))}
          </div>
        </TailAdminPanel>
      ) : null}

      <TailAdminPanel title="Content coach" description="Rewrite messages — you send them">
        <div className="mb-2 flex gap-2">
          {["whatsapp", "reddit", "x"].map((c) => (
            <Button
              key={c}
              size="sm"
              variant={channel === c ? "default" : "outline"}
              onClick={() => setChannel(c)}
            >
              {c}
            </Button>
          ))}
        </div>
        <Textarea
          placeholder="Paste your draft message..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          className="mb-2"
        />
        <Button onClick={handleCoach} disabled={coachingLoading} className="gap-2">
          <Sparkles className="h-4 w-4" />
          {coachingLoading ? "Coaching..." : "Improve draft"}
        </Button>
        {coaching ? (
          <div className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
            <p className="font-medium text-gray-800">Suggested message</p>
            <p className="whitespace-pre-wrap text-gray-700">{coaching.improved}</p>
            {coaching.warnings?.length > 0 ? (
              <p className="text-xs text-amber-700">Warnings: {coaching.warnings.join(", ")}</p>
            ) : null}
          </div>
        ) : null}
      </TailAdminPanel>
    </div>
  );
};

export default NavigatorPanel;
